/**
 * Client for the AI orchestrator (feature 0059 lot 1d) — POST /api/store/ai/generate.
 *
 * ONE section per call: the backend caps slots per call, and the construction scene reveals section by
 * section, so the call granularity IS the UX granularity. The backend knows models and costs; this side
 * knows documents and schemas — the slot `key` is opaque to the server and round-trips unchanged
 * (see `state/generation/slotKeys.ts` for the encoding).
 *
 * The two status codes that are CONTRACT, not accidents (AiController's KDoc):
 *  - 503 → the orchestrator is not configured on this env. Typed as {@link AiUnavailableError} so the
 *    caller stops the whole run and hides the AI entry — retrying per-section would 503 thirty times.
 *  - 502 → the provider refused or is down for THIS call. Typed as {@link AiGenerationError}: the
 *    caller keeps the theme copy for this section and keeps going (the safety-net rule, 0059 §5.3).
 */
import { apiRequest } from './http';
import { deriveApiBaseUrl } from '../boot/config';
import { getToken } from '../boot/session';
import { HttpError } from './errors';

/** Mirrors the backend's SlotDto — sizes are validated server-side, truncate BEFORE sending. */
export interface AiSlotDto {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'richtext';
  maxLength?: number;
  current: string;
}

export interface AiGenerateRequest {
  capability: 'SECTION';
  locale: string;
  voice: string;
  role: string;
  storeName: string;
  storeCategory: string;
  storeDescription: string;
  productContext?: string;
  /**
   * LE PRODUIT DE CETTE PAGE (27/08) — le serveur s'en sert pour aller chercher le RÉSUMÉ de son
   * étude (ce que les vraies clientes ont dit) et le coller au contexte. Absent, ou sans étude
   * prête : la retouche écrit exactement comme avant, sans un mot inventé pour combler.
   *
   * On n'envoie PAS l'étude d'ici : elle fait des dizaines de kilo-octets, et le contexte est borné
   * à 3 000 caractères côté serveur. C'est lui qui a la ligne sous la main.
   */
  productId?: string;
  slots: AiSlotDto[];
  /** RETOUCHE (lot 2, «Ask for changes»): keep the current copy's subject, apply the instruction. */
  rewrite?: boolean;
  /**
   * L'IA DEMANDÉE POUR CE PROMPT (« Demander un changement », 26/08) — un des fournisseurs dont la
   * vendeuse a posé la clé. Absent = la chaîne rangée dans ses Réglages. Un fournisseur qui ne sait
   * pas écrire est refusé par le serveur (400) ; sans clé chez lui, il est ignoré sans casser.
   */
  provider?: string;
}

export interface AiGenerateResponse {
  values: Record<string, string>;
  model: string;
}

/** 503 — the module has no key on this env. Ends the run; not a per-section condition. */
export class AiUnavailableError extends Error {
  constructor() {
    super('AI generation is not configured');
    this.name = 'AiUnavailableError';
  }
}

/** 502 (or any other failure) — this one call failed; the theme copy stands for its section. */
export class AiGenerationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AiGenerationError';
  }
}

export interface AiGenerateImageRequest {
  prompt: string;
  aspectRatio: '16:9' | '4:3' | '1:1' | '3:4' | '9:16';
  /** L'IA demandée pour CE dessin — voir {@link AiGenerateRequest.provider}. */
  provider?: string;
}

const GENERATE_PATH = '/api/store/ai/generate';
const GENERATE_IMAGE_PATH = '/api/store/ai/generate-image';
const GENERATE_PRODUCT_IMAGE_PATH = '/api/store/ai/generate-product-image';
const COMPOSE_PATH = '/api/store/ai/compose';
const GENERATE_IMAGE_STREAM_PATH = '/api/store/ai/generate-image-stream';

/** Parse a fetch()ed SSE body: calls `onEvent(name, data)` for each complete frame. */
async function consumeSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (name: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf('\n\n');
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let name = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length > 0) onEvent(name, dataLines.join('\n'));
      sep = buffer.indexOf('\n\n');
    }
  }
}

export interface AiComposeRequest {
  description: string;
  productContext: string;
  sections: { id: string; purpose: string }[];
}

export interface AiComposeResponse {
  product: string;
  index: string;
  degraded?: boolean;
}

export const aiApi = {
  /**
   * The marketing-expert composer (founder 2026-08-21): the full component menu goes up, ordered
   * per-page section lists come back. Throws on transport/503 — the caller falls back to the
   * fixed blueprint, never blocks.
   */
  async compose(request: AiComposeRequest): Promise<AiComposeResponse> {
    return apiRequest<AiComposeResponse>(COMPOSE_PATH, { method: 'POST', body: request });
  },

  async generateSection(request: AiGenerateRequest): Promise<AiGenerateResponse> {
    try {
      return await apiRequest<AiGenerateResponse>(GENERATE_PATH, {
        method: 'POST',
        body: request,
      });
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 503) throw new AiUnavailableError();
      throw new AiGenerationError(e instanceof Error ? e.message : 'generation failed');
    }
  },

  /**
   * One decor image (Higgsfield interim, 0059). Gated SEPARATELY from text server-side: a 503 here
   * means "no image key on this env" — the run turns images off and keeps writing text.
   */
  async generateImage(request: AiGenerateImageRequest): Promise<string> {
    try {
      const res = await apiRequest<{ url: string }>(GENERATE_IMAGE_PATH, {
        method: 'POST',
        body: request,
      });
      return res.url;
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 503) throw new AiUnavailableError();
      throw new AiGenerationError(e instanceof Error ? e.message : 'image generation failed');
    }
  },

  /**
   * STREAMED page image (founder 2026-08-21): partial frames arrive through `onPartial` as data URIs
   * the canvas can paint immediately; resolves with the final re-hosted URL. `productId` set → the
   * product's cover is the server-side visual reference (the image shows THE product). Throws
   * AiUnavailableError on 503; any other failure throws AiGenerationError — the caller may retry
   * through the plain endpoint.
   */
  async generateImageStream(
    request: AiGenerateImageRequest & { productId?: string },
    onPartial: (dataUri: string) => void,
  ): Promise<string> {
    const token = getToken();
    const res = await fetch(`${deriveApiBaseUrl()}${GENERATE_IMAGE_STREAM_PATH}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
    });
    if (res.status === 503) throw new AiUnavailableError();
    if (!res.ok || !res.body) throw new AiGenerationError(`stream refused (${res.status})`);
    let finalUrl: string | null = null;
    let failure: string | null = null;
    await consumeSse(res.body, (name, data) => {
      try {
        const payload = JSON.parse(data) as { dataUri?: string; url?: string; reason?: string };
        if (name === 'partial' && payload.dataUri) onPartial(payload.dataUri);
        else if (name === 'done' && payload.url) finalUrl = payload.url;
        else if (name === 'failed') failure = payload.reason ?? 'generation failed';
      } catch {
        // a malformed frame is dropped; the stream continues
      }
    });
    if (finalUrl) return finalUrl;
    throw new AiGenerationError(failure ?? 'stream ended without an image');
  },

  /**
   * One PRODUCT marketing shot (founder 2026-08-21): generated, re-hosted, and SAVED server-side into
   * the product's media — the returned URL is already a real product photo.
   */
  async generateProductImage(
    request: AiGenerateImageRequest & { productId: string },
  ): Promise<string> {
    try {
      const res = await apiRequest<{ url: string }>(GENERATE_PRODUCT_IMAGE_PATH, {
        method: 'POST',
        body: request,
      });
      return res.url;
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 503) throw new AiUnavailableError();
      throw new AiGenerationError(
        e instanceof Error ? e.message : 'product image generation failed',
      );
    }
  },
};
